/**
 * The local database — the UI's only source of truth (W-2).
 *
 * Every render reads from here, which is what makes the app instant and what
 * makes INV-4 achievable: a capture write is an IndexedDB put, and it returns
 * before the network has been consulted at all.
 *
 * IDs are generated on the client with `crypto.randomUUID()`. An expense created
 * in airplane mode must get its real, final id immediately — a placeholder that
 * gets rewritten on sync would break every local reference to it.
 */

import Dexie, { type Table } from "dexie";

import { EXPENDITURE_LABEL } from "@/domain/category_kind";
import type {
  LinkedSplitRow,
  LocalSplitGroup,
  LocalSplitExpense,
  LocalSplitTransfer,
  LocalBudget,
  LocalCategory,
  LocalExpense,
  LocalProfile,
  LocalRecurringRule,
  LocalSalary,
  LocalSounding,
  LocalSoundingAction,
} from "./types";

/** Sync bookkeeping and other singletons, keyed by name. */
export interface MetaRow {
  key: string;
  value: string;
}

/*
 * There is deliberately no shared "every table" constant here any more.
 *
 * There was one, and its only consumer was the v3 upgrade — which meant adding a
 * table to the present-day list sent a migration written for v3 looking for a
 * store that would not exist for another two versions, and every database on
 * disk failed to open. A migration describes the schema as it was; it cannot
 * share a list with the schema as it is. `clearLocalData` clears the current stores through their own transactions;
 * migrations still describe only the stores that existed at that version.
 */
export class AegaeDb extends Dexie {
  declare profile: Table<LocalProfile, string>;
  declare categories: Table<LocalCategory, string>;
  declare budgets: Table<LocalBudget, string>;
  declare salaries: Table<LocalSalary, string>;
  declare recurringRules: Table<LocalRecurringRule, string>;
  declare expenses: Table<LocalExpense, string>;
  declare soundings: Table<LocalSounding, string>;
  declare soundingActions: Table<LocalSoundingAction, string>;
  declare splitGroups: Table<LocalSplitGroup, string>;
  declare splitExpenses: Table<LocalSplitExpense, string>;
  declare splitTransfers: Table<LocalSplitTransfer, string>;
  declare linkedSplitRows: Table<LinkedSplitRow, [string, string, string]>;
  declare meta: Table<MetaRow, string>;

  constructor(name = "aegae") {
    super(name);
    this.version(1).stores({
      profile: "id, _dirty",
      categories: "id, sortOrder, _dirty, updatedAt",
      budgets: "id, categoryId, effectiveFrom, _dirty, updatedAt",
      // `occurredAt` is an ISO string: lexicographic order is chronological
      // order, so Dexie range queries over a period work directly on the index.
      expenses: "id, occurredAt, categoryId, _dirty, updatedAt, deletedAt",
      recurringRules: "id, categoryId, _dirty, updatedAt",
      meta: "key",
    });
    // v2 held the derived vault key, back when rows were encrypted before they
    // were pushed.
    this.version(2).stores({ vault: "id" });

    /**
     * v3 drops the vault **and clears every table** — deliberately, not as
     * cleanup.
     *
     * Aegae left leeseidon's Worker, D1 and user accounts. A local row here was
     * written against a leeseidon `account_id` that this app can no longer
     * resolve, and `sync:since` points into a `seq` space that no longer exists.
     * Keeping either would push rows stamped with a foreign user id into a fresh
     * account and then step the cursor past everything the server actually holds.
     *
     * There is nothing to migrate: the `aegae_*` rows on leeseidon's D1 were
     * already unreadable — a shared vault salt was rotated three times on
     * 2026-07-22, orphaning them — and have since been removed. A clean slate is
     * the honest state, and pretending otherwise would show a ledger that can
     * never sync.
     */
    this.version(3)
      .stores({ vault: null })
      .upgrade(async (tx) => {
        // Frozen at what existed in v3, and deliberately NOT `DATA_TABLES`.
        // A past migration must describe the past: pointing it at the current
        // list meant that adding `soundings` in v5 made the v3 upgrade try to
        // clear a table two versions before it exists, and every v2 database on
        // disk failed to open. A migration that changes when today's schema
        // changes is not a migration.
        const TABLES_AT_V3 = [
          "profile",
          "categories",
          "budgets",
          "recurringRules",
          "expenses",
          "meta",
        ];
        for (const table of TABLES_AT_V3) await tx.table(table).clear();
      });

    /**
     * v4 adds three optional, **unindexed** category fields — `note`,
     * `savingsOrExpenditure` and `keepInAccount` — for the budget-sheet
     * round-trip (DATA_MODEL §2, §8). They are not indexed, so the store schema
     * is unchanged (the `categories` string is re-stated identically); this
     * version exists only to backfill existing rows to `null`, so every budget
     * export is uniform rather than sprouting keys partway down the file.
     *
     * Purely additive — no data is cleared.
     *
     * > This backfill wrote `null`, which was correct then and is the reason v6
     * > exists. From v6 a null `savingsOrExpenditure` means **savings**, so every
     * > row this pass touched has to be stamped again. The sentence that used to
     * > sit here — "it carries no behaviour, so nothing that renders a number
     * > depends on the backfill having run" — has not been true since.
     */
    this.version(4)
      .stores({ categories: "id, sortOrder, _dirty, updatedAt" })
      .upgrade(async (tx) => {
        const categories = tx.table("categories") as unknown as Table<
          Record<string, unknown>,
          string
        >;
        await categories.toCollection().modify((category) => {
          if (category.note === undefined) category.note = null;
          if (category.savingsOrExpenditure === undefined) category.savingsOrExpenditure = null;
          if (category.keepInAccount === undefined) category.keepInAccount = null;
        });
      });

    /**
     * v5 adds the agent's memory: `soundings` (conversations, cleared after
     * three days) and `soundingActions` (what it saved, kept forever).
     *
     * Two tables rather than one because they have **different lifetimes**, and
     * that is the whole design: the transcript is the bulky, private, disposable
     * half, and "12 expenses added from a statement" is the half worth keeping.
     * Folding them together would mean choosing one retention for both.
     *
     * `soundings` indexes `updatedAt`, which both the three-day sweep (a range
     * query) and the history list (an ordered read) go through.
     * `soundingActions` indexes `at`, which is how its list is ordered. Neither
     * indexes anything else: `deletedAt` and `soundingId` were both in an
     * earlier draft, justified in this comment by reads that did not exist. An
     * index nothing queries is not free — it is a claim about how the data is
     * used that somebody later trusts.
     *
     * It also backfills `sourceId` on existing expenses, so every row in the
     * export has the key rather than growing one partway down the file. Purely
     * additive; nothing is cleared.
     */
    this.version(5)
      .stores({
        soundings: "id, updatedAt, _dirty",
        soundingActions: "id, at, _dirty, updatedAt",
      })
      .upgrade(async (tx) => {
        const expenses = tx.table("expenses") as unknown as Table<
          Record<string, unknown>,
          string
        >;
        await expenses.toCollection().modify((expense) => {
          if (expense.sourceId === undefined) expense.sourceId = null;
        });
      });

    /**
     * v6 adds `salaries` and stamps every unlabelled category as `"Expenditure"`.
     *
     * The backfill is **not cleanup, and must not be dropped as redundant.**
     * From this version `savingsOrExpenditure` drives math, and the rule is that
     * expenditure must be *stated*: blank means savings
     * (`src/domain/category_kind.ts`). Every row written before this version
     * holds `null`, because that is what v4 backfilled and what `addCategory`
     * wrote. Without this pass, the first load after upgrading reclassifies every
     * category in the account as savings — the numpad empties down to the
     * fallback and every expense ever logged leaves the expenditure history at
     * once.
     *
     * Rows written before the rule existed were never *labelled* anything. This
     * records what they have always meant, and only then does the rule bite.
     *
     * It deliberately leaves `updatedAt` and `_dirty` **alone**, and does not
     * try to push the stamp. Two reasons, and the second is why an earlier draft
     * of this was wrong:
     *
     *   - It does not need to. `fromWireCategory` repairs a null on the way in,
     *     so every device fixes itself on its own first pull — including one
     *     starting from an empty database, which runs no upgrade at all and
     *     which a pushed stamp could therefore never have helped.
     *   - Freshening `updatedAt` on rows the user has not touched is an
     *     unprovoked last-write-wins bid. A device that had not synced in a week
     *     would push week-old copies stamped "now" and silently beat a rename
     *     made yesterday on another device.
     *
     * `salaries` indexes `effectiveFrom` because that is how the version in force
     * is resolved, mirroring `budgets`. Purely additive; nothing is cleared.
     */
    this.version(6)
      .stores({ salaries: "id, effectiveFrom, _dirty, updatedAt" })
      .upgrade(async (tx) => {
        const categories = tx.table("categories") as unknown as Table<
          Record<string, unknown>,
          string
        >;
        await categories.toCollection().modify((category) => {
          // A non-string here is a corrupt row rather than a decision the user
          // made, so it gets stamped alongside the nulls rather than being
          // trusted enough to classify a category as savings.
          const label = category.savingsOrExpenditure;
          if (typeof label === "string" && label.trim() !== "") return;
          category.savingsOrExpenditure = EXPENDITURE_LABEL;
        });
      });

    /**
     * v7 adds `Category.icon` and `Profile.defaultCategoryId`, both unindexed,
     * so no store changes; this version exists for the backfill.
     *
     * The eight seeded categories get their icons, matched by seeded name; any
     * other category gets `null`, as does every profile's default. An icon
     * already present (pulled from a newer device) is left alone.
     *
     * **Nothing is marked dirty and `updatedAt` does not move.** Every device
     * runs this same upgrade, and `categoryIcon()` draws the same seeded icon
     * for a row that never got one, so there is nothing another device needs
     * to be told — and marking eight rows dirty on every install would be a
     * burst of uploads that changes nothing anyone sees. The server's copy
     * gains the icon on the row's next real edit.
     *
     * The icon list is frozen here rather than imported, for the reason v3
     * gives: a migration describes the past, and must not change meaning when
     * the seed list does.
     */
    this.version(7).stores({ salaries: "id, effectiveFrom, _dirty, updatedAt" }).upgrade(async (tx) => {
      const ICONS_AT_V7: Readonly<Record<string, string>> = {
        "Food & Drink": "🍜",
        Groceries: "🛒",
        Transport: "🚌",
        Bills: "💡",
        Shopping: "🛍️",
        Fun: "🎟️",
        Health: "💊",
        Other: "•••",
      };
      const categories = tx.table("categories") as unknown as Table<Record<string, unknown>, string>;
      await categories.toCollection().modify((category) => {
        // Repeat v6's local-only label stamp for installs that ran the old icons-only v6 (see v6 above).
        const label = category.savingsOrExpenditure;
        if (typeof label !== "string" || label.trim() === "") category.savingsOrExpenditure = EXPENDITURE_LABEL;
        if (category.icon !== undefined) return;
        const name = typeof category.name === "string" ? category.name : "";
        category.icon = ICONS_AT_V7[name] ?? null;
      });
      const profiles = tx.table("profile") as unknown as Table<Record<string, unknown>, string>;
      await profiles.toCollection().modify((profile) => {
        if (profile.defaultCategoryId === undefined) profile.defaultCategoryId = null;
      });
    });
    this.version(8).stores({ expenses: "id, categoryId, occurredAt, _dirty, updatedAt, deletedAt, _moveFrom.kind" });
    // Additive only: existing rows and dirty/version metadata remain untouched.
    this.version(9).stores({
      splitGroups: "id, _dirty, updatedAt, deletedAt",
      splitExpenses: "id, groupId, occurredAt, _dirty, updatedAt, deletedAt",
      splitTransfers: "id, groupId, _dirty, updatedAt, deletedAt",
      expenses: "id, categoryId, occurredAt, _dirty, updatedAt, deletedAt, _moveFrom.kind, splitRef.splitExpenseId",
      linkedSplitRows: "[linkId+table+rowId], linkId",
    });
  }
}

export const db = new AegaeDb();
export const familyDb = new AegaeDb("aegae-family");

/* ── meta helpers ──────────────────────────────────────────────────────────── */

export async function getMeta(key: string): Promise<string | null> {
  const row = await db.meta.get(key);
  return row?.value ?? null;
}

export async function setMeta(key: string, value: string): Promise<void> {
  await db.meta.put({ key, value });
}

/* ── whose ledger is this? ─────────────────────────────────────────────────── */

/**
 * The account this device's ledger belongs to, or `null` if there is nothing
 * here to protect.
 *
 * **This database is not partitioned by account.** There is one set of tables,
 * and `clearLocalData()` on sign-out is the only thing that has ever enforced
 * one-account-per-device. That is fine while the sole route to a sign-in screen
 * wipes the device — but it stops being fine the moment a session can be
 * re-established over an existing ledger, because `pushSpec` uploads every dirty
 * row it finds and the server stamps them with whichever account presented the
 * bearer token. One person's expenses would land in another's account.
 *
 * So: read the owner, compare, and wipe on a mismatch (`adoptReauthenticatedSession`).
 *
 * The profile row is keyed on the account id (`updateProfile` uses
 * `requireUser()`), which makes it the cheapest answer. But categories are
 * seeded as soon as the first sync settles — before the install gate ever calls
 * `updateProfile` — so a device can hold a real ledger with no profile row yet,
 * and the fallback is not hypothetical.
 */
export async function localLedgerAccountId(store: AegaeDb = db): Promise<string | null> {
  const profile = await store.profile.toCollection().first();
  if (profile) return profile.id;

  // Every remaining table carries `userId`, and all of them are checked rather
  // than just `categories`: a device that has been through a partial wipe, or
  // whose seeding never completed, can hold expenses with no categories.
  // Reading `null` there would skip the ownership check entirely — and the
  // agent's two tables are in the list for the same reason, since a device can
  // hold a conversation before a pull has settled anything else.
  for (const table of [
    store.categories,
    store.expenses,
    store.budgets,
    store.salaries,
    store.recurringRules,
    store.soundings,
    store.soundingActions,
    store.splitGroups,
    store.splitExpenses,
    store.splitTransfers,
  ]) {
    const row = await table.toCollection().first();
    if (row) return row.userId;
  }
  return null;
}

/**
 * Wipe every local table. Used on sign-out, so a shared device does not leave
 * one user's spending history readable to the next.
 *
 * The sync cursor goes with it (it lives in `meta`). It must: a cursor kept
 * across a sign-in would start the next account's first pull partway through
 * a `seq` space it has never seen, and every row before that point would simply
 * never arrive.
 */
export async function clearLocalData(): Promise<void> {
  // Decided by whether the store exists, not by a cached id: a family store that holds rows
  // must never survive sign-out on a shared device. Unknown counts as present (fail closed).
  const clearFamily = familyDb.isOpen() || await Dexie.exists(familyDb.name).catch(() => true);
  let firstError: Error | undefined;
  for (const store of clearFamily ? [db, familyDb] : [db]) {
    try { await clearDatabase(store); } catch (cause) { firstError ??= cause instanceof Error ? cause : new Error(String(cause)); }
  }
  if (firstError !== undefined) throw firstError;
}

export function newId(): string {
  return crypto.randomUUID();
}

export function nowIso(): string {
  return new Date().toISOString();
}

/** Explicit store wipe, used only after an authorized leave/discard or sign-out. */
export async function clearDatabase(store: AegaeDb): Promise<void> {
  await store.transaction("rw", store.tables, async () => {
    await Promise.all(store.tables.map(table => table.clear()));
  });
}
